import { makeStyles, Button, Tooltip, Typography, useTranslation, FormSectionTitle, Collapse } from '@paintscout/ui'
import type { MuiTheme as Theme, StyleClasses } from '@paintscout/ui'
import { DropdownSelect, useClientOptions } from '@paintscout/ui-business'
import type { DropdownSelectOption } from '@paintscout/ui-business'
import React, { useMemo, useState } from 'react'
import classnames from 'classnames'
import type { QuoteItemSection, QuoteItemView, RenderableItem } from '@paintscout/util/builder'
import { getDetails, getFeature, getItems, getQuoteOptions } from '@paintscout/util/builder'
import type { QuoteOptions } from 'paintscout'
import WorkOrderCard from '../WorkOrderCard'
import { useQuote } from '../../../../context'

const useStyles = makeStyles<Theme, WorkOrderTableProps>(
  (theme) => ({
    root: {},
    avoidPageBreak: {
      '@media print': {
        pageBreakInside: 'avoid',
        pageBreakAfter: 'always'
      }
    },
    readOnly: {},

    hoursToggle: {
      maxWidth: '175px'
    },

    hidePrint: {
      '@media print': {
        display: 'none'
      }
    },

    li: {
      '&::before': {
        content: "''",
        color: 'white'
      },
      '@media print': {
        gridTemplateColumns: '1fr 1fr',
        gridGap: theme.spacing(2)
      }
    },
    cards: {
      listStyleType: 'none',
      paddingLeft: 0,
      [theme.breakpoints.up('lg')]: {
        display: 'grid',
        gridTemplateColumns: '1fr 1fr',
        gridGap: theme.spacing(2)
      },
      '@media print': {
        display: 'grid',
        gridTemplateColumns: '1fr 1fr',
        gridGap: theme.spacing(2)
      }
    },
    singleColumnCards: {
      gridTemplateColumns: '1fr',
      '@media print': {
        display: 'block'
      },
      '& li': {
        '@media print': {
          marginBottom: theme.spacing(2)
        }
      }
    },
    avoidPageBreakLi: {
      '@media print': {
        breakInside: 'avoid'
      }
    },
    avoidPageBreakLiNested: {
      '& li': {
        '@media print': {
          pageBreakInside: 'avoid'
        }
      }
    },
    paginationDiv: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      borderBottom: `1px solid ${theme.palette.divider}`,
      paddingBottom: theme.spacing(1),
      marginBottom: theme.spacing(2)
    }
  }),
  { name: 'WorkOrderTable' }
)

export interface WorkOrderTableProps {
  classes?: StyleClasses<typeof useStyles>
  onReorder?: (args: { items: RenderableItem[]; section: QuoteItemSection; view: QuoteItemView }) => void
  onAddClick?: (event: React.MouseEvent<HTMLElement>) => void
  onItemClick?: (event: React.MouseEvent<Element, MouseEvent>, key: string) => void
  onToggle?: (ev: React.SyntheticEvent) => void
  onViewChange?: (event: React.SyntheticEvent, view: string) => void
  onToggleAmount?: (dropdownValue: string) => void
  tableView?: QuoteItemView
  section?: QuoteItemSection
  showPrices?: boolean
  showHours?: boolean
  showTotalHours?: boolean
  title?: string
  subTitle?: string
  collapsed?: boolean

  showAddButton?: boolean
  showEmpty?: boolean
  showLineItems?: boolean
  rightContent?: React.ReactNode
}

function WorkOrderTable(props: WorkOrderTableProps) {
  const classes = useStyles(props)
  const {
    section,
    title,
    subTitle,
    collapsed,
    tableView,
    showEmpty,
    onToggle,
    onToggleAmount,
    showHours,
    showTotalHours,
    showPrices,
    showLineItems
  } = props
  const { quote, isEditable } = useQuote()
  const { options } = useClientOptions()
  const { __ } = useTranslation()
  const details = useMemo(() => getDetails({ quote, options }), [quote, options])
  const quoteOptions = getQuoteOptions({ options, quote })
  const noPdfPageBreak = getFeature({ options, path: 'quotes.noPdfPageBreak' })
  const columns = options.options.quotes.singleColumnWorkOrder === true ? 1 : 2
  const allowShowHours = !details.totals.hours.useCustom || quoteOptions.allowShowCustomizedTotalHours
  const paginate = Number(quote?.paginateWorkOrder ?? 0)
  const [selectedAccount, setSelectedAccount] = useState(getDefaultToggleOption(quoteOptions))
  const [start, setStart] = useState(0)
  const [end, setEnd] = useState(start + paginate)

  const items = useMemo(() => {
    let items = getItems({
      quote,
      section,
      view: tableView,
      consumer: 'crew',
      options,
      overrideOptions: { showQuantities: true },
      showLineItems,
      showValues: showHours,
      hideDimensions: quoteOptions.hideWorkOrderDimensions
    })

    if (section === 'bid') {
      const addedItems = getItems({
        quote,
        section: 'added-options',
        view: tableView,
        consumer: 'crew',
        options,
        overrideOptions: { showQuantities: true },
        showLineItems,
        showValues: showHours,
        hideDimensions: quoteOptions.hideWorkOrderDimensions
      })

      items = [...items, ...addedItems]
    }
    return items
  }, [quote, tableView])
  const totalItems = items.length

  if ((collapsed && !isEditable) || (!showEmpty && !totalItems)) {
    return null
  }

  const toggleOptions: DropdownSelectOption[] = [
    {
      value: 'showAllHours',
      label: __('Show all hours')
    },
    {
      value: 'hideItemHours',
      label: __('Hide item hours')
    },
    // {
    //   value: 'hideTotalHours',
    //   label: 'Hide total hours'
    // },
    {
      value: 'hideAllHours',
      label: __('Hide all hours')
    }
  ]

  const defaultRightContent =
    onToggleAmount && isEditable ? (
      <>
        <DropdownSelect
          variant="single"
          className={classes.hoursToggle}
          options={toggleOptions}
          value={toggleOptions.find((option) => option.value === selectedAccount)}
          onChange={(option: DropdownSelectOption) => {
            setSelectedAccount(option.value)
            onToggleAmount(option.value)
          }}
          disabled={!allowShowHours}
        />
        {!allowShowHours && <Tooltip title={__('Hours cannot be shown because Total Hours have been customized')} />}
      </>
    ) : null

  const rightContent = props.rightContent ? props.rightContent : defaultRightContent

  const handleItemClick = (ev: React.MouseEvent<Element, MouseEvent>, key: string) => {
    if (props?.onItemClick) {
      props.onItemClick(ev, key)
    }
  }

  const scrollToTop = () => {
    const element = document.getElementById(`work-order-section-${title}`)
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'start' })
    }
  }

  return (
    <div
      className={classnames({
        [classes.avoidPageBreak]: !noPdfPageBreak,
        [classes.readOnly]: !isEditable,
        [classes.hidePrint]: !items.length
      })}
      style={{ position: 'relative' }}
    >
      <div id={`work-order-section-${title}`} style={{ position: 'absolute', top: '-100px' }}></div>
      {title && (
        <FormSectionTitle
          title={title}
          subTitle={subTitle}
          showToggle={isEditable && !!onToggle}
          toggleValue={!collapsed}
          onToggle={onToggle}
          rightContent={rightContent}
        />
      )}

      <Collapse show={!collapsed}>
        <ul
          className={classnames(
            classes.cards,
            columns === 1 ? classes.singleColumnCards : null,
            noPdfPageBreak ? null : classes.avoidPageBreakLiNested
          )}
        >
          {(paginate ? items.slice(start, end) : items).map((item: RenderableItem, index: number) => {
            return (
              <li className={classnames(classes.li, noPdfPageBreak ? null : classes.avoidPageBreakLi)} key={index}>
                <WorkOrderCard
                  item={item}
                  showHours={showHours}
                  showTotalHours={showTotalHours}
                  showPrices={showPrices && section === 'options'}
                  quote={quote}
                  section={section}
                  onClick={(ev) => handleItemClick(ev, item.key)}
                />
              </li>
            )
          })}
        </ul>
        {paginate && totalItems > paginate ? (
          <div className={classes.paginationDiv}>
            <Button
              variant="text"
              disabled={start === 0}
              onClick={() => {
                const newStart = Math.max(0, start - paginate)
                const newEnd = newStart + paginate
                setStart(newStart)
                setEnd(newEnd)
                scrollToTop()
              }}
            >
              {__('Previous')}
            </Button>

            <Typography variant="subtitle2">
              {start + 1} - {Math.min(end, totalItems)} of {totalItems}
            </Typography>

            <Button
              variant="text"
              disabled={end >= totalItems}
              onClick={() => {
                const newEnd = Math.min(totalItems, end + paginate)
                const newStart = newEnd - paginate
                setEnd(newEnd)
                setStart(newStart)
                scrollToTop()
              }}
            >
              {__('Next')}
            </Button>
          </div>
        ) : (
          <></>
        )}
      </Collapse>
    </div>
  )
}

export default WorkOrderTable

function getDefaultToggleOption(quoteOptions: QuoteOptions): string {
  const itemKeys = [
    'showWorkOrderAreaValues',
    'showWorkOrderSubstrateValues',
    'showWorkOrderAreaOptionValues',
    'showWorkOrderSubstrateOptionValues'
  ]
  const allTrue = itemKeys.every((key) => quoteOptions[key] === true)
  const allFalse = itemKeys.every((key) => quoteOptions[key] === false)
  if (allTrue) {
    if (quoteOptions.showWorkOrderTotalHours === false) {
      return 'hideTotalHours'
    } else {
      return 'showAllHours'
    }
  } else if (allFalse) {
    if (quoteOptions.showWorkOrderTotalHours === true) {
      return 'hideItemHours'
    } else {
      return 'hideAllHours'
    }
  } else {
    return 'showAllHours'
  }
}
